import { Injectable, inject } from "@angular/core";
import { Router } from "@angular/router";
import { AlertController } from "@ionic/angular";
import { CookieNames } from "@models/CookieNames";
import { AutologinStatus } from "@models/I_DecouplingLogin";
import { LicenseTypes } from "@models/MasterData";
import { GlobalVarsModel } from "@models/ModelGlobalsVar";
import { DataUserMSOService, GraceTimeStatus } from "@mso-services/dataUserMSO.services";
import { UtilsMsoService } from "@mso-services/utilsMso.service";
import { TranslateService } from "@ngx-translate/core";
import { CookiesService } from "@providers/CookiesService";
import { DecouplingUserServiceMSO } from "@providers/DecouplingUserServiceMSO.service";
import { EventsService } from "@providers/EventsService";
import { NewLoginService } from "@providers/NewLogin.service";
import { UserServiceMSO } from "@providers/UserServiceMSO";
import { NewBaseService } from "@providers/newBase.service";
import { firstValueFrom } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class RegistroService {
  private globalVars: GlobalVarsModel;

  private decouplingUserServiceMSO = inject(DecouplingUserServiceMSO);
  private userServiceMSO = inject(UserServiceMSO);
  private dataUserService = inject(DataUserMSOService);
  private utilsMsoService = inject(UtilsMsoService);
  private newLoginService = inject(NewLoginService);
  private newBaseService = inject(NewBaseService);
  private cookiesService = inject(CookiesService);
  private events = inject(EventsService);
  private alertCtrl = inject(AlertController);
  private router = inject(Router);
  private translate = inject(TranslateService);

  constructor() {
    this.newBaseService.getVars.subscribe((vars) => {
      this.globalVars = vars;
    });
  }

  private doPreSignupCall(contactId: string) {
    this.decouplingUserServiceMSO.postSignup(contactId).subscribe({
      next: () => {
        /** empty */
      },
      error: () => {
        /** empty */
      },
    });
  }

  private requestComplianceCO() {
    const request = this.globalVars.FEATURES.MSO_NewDecouplingServiceSignup
      ? this.decouplingUserServiceMSO.decouplingRegisterCOComplianceRecords()
      : this.userServiceMSO.registerCOComplianceRecords();
    firstValueFrom(request).then(async (data) => {
      this.globalVars.extraUserData.pptNotVerification = data.presentaRiesgo;
      if (data.presentaRiesgo) {
        const alertCompliance = await this.alertCtrl.create({
          header: this.translate.instant("warning").toUpperCase(),
          message: this.translate.instant("accountadditionalvalidation"),
          buttons: [
            {
              text: "OK",
              handler: () => {
                this.events.publish("closeWelcomeBonus");
                this.events.publish("user:logout");
              },
            },
          ],
          backdropDismiss: false,
        });
        alertCompliance.present();
      }
    });
  }

  private beforeAutoLoginRequests() {
    if (this.globalVars.FEATURES.MSO_EnabledComplianceRegistroCO) {
      this.requestComplianceCO();
    }
    if (this.globalVars.FEATURES.MSO_Post_Signup_Decoupling) {
      this.doPreSignupCall(this.globalVars.user.id);
    }
  }

  public loginAndGoto(dest = null, params = null, doSetRoot = true) {
    this.beforeAutoLoginRequests();
    return new Promise<boolean>((resolve) => {
      if (this.globalVars.FEATURES.MSO_DecouplingLogin) {
        this.newLoginService
          .doAutoLoginCookie(() => {
            this.cookiesService.setCookie(
              CookieNames.CodereUser,
              this.globalVars.user.username,
              this.globalVars.rootScope.cookiesService.YEAR_MILLISECONDS,
            );
            if (this.globalVars.licenseType === LicenseTypes.Nacional) {
              this.cookiesService.setCookiePerUser(
                CookieNames.MigrationCompleted,
                this.globalVars.user.username,
                this.globalVars.rootScope.cookiesService.YEAR_MILLISECONDS,
              );
            }

            if (this.globalVars.licenseType === LicenseTypes.Panama) {
              localStorage.setItem("paramsPaAfterReg", JSON.stringify({ comeFromReg: true }));
            }

            if (this.globalVars.licenseType === LicenseTypes.Colombia) {
              if (this.globalVars.partner === "mvp") {
                this.globalVars.mvpData.login = "1";
                sessionStorage.setItem(
                  "partnercodere",
                  JSON.stringify({ partner: this.globalVars.partner, mvpData: this.globalVars.mvpData }),
                );
              }
            }
            if (doSetRoot) {
              this.router.navigate([dest]);
            }

            this.events.publish("user:login");

            if (!this.globalVars.FEATURES.ModalBonusSportCasino) {
              this.events.publish(
                "cashier:open",
                params
                  ? { cashierEmpty: false, section: "deposit", ...params }
                  : { cashierEmpty: false, section: "deposit" },
              );
            }
          })
          .then((logged) => {
            resolve((logged as AutologinStatus) === AutologinStatus.LOGGED);
          });
      } else {
        this.globalVars.rootScope.userService.getCurrentUser().subscribe(
          async (data: any) => {
            if (data.sessionOut) {
              resolve(false);
            } else {
              if (data.success) {
                if (data.idBGT) {
                  this.globalVars.user.idBGT = data.idBGT;
                }
                this.globalVars.user.username = data.username;
                this.globalVars.user.balance = data.balance;
                this.globalVars.sessionInitDate = new Date();
                this.globalVars.user.logged = true;
                this.globalVars.rootScope.userService.userLogged = true;
                let auxRegion: number;
                if (data.license) {
                  switch (data.license) {
                    case "Colombia":
                      auxRegion = LicenseTypes.Colombia;
                      break;
                    case "Panama":
                      auxRegion = LicenseTypes.Panama;
                      break;
                    case "Nacional":
                      auxRegion = LicenseTypes.Nacional;
                      break;
                    case "ArgentinaCaba":
                      auxRegion = LicenseTypes.ArgentinaCaba;
                      break;
                    case "ArgentinaPBA":
                      auxRegion = LicenseTypes.ArgentinaPBA;
                      break;

                    default:
                      auxRegion = this.globalVars.licenseType;
                  }
                } else {
                  auxRegion = this.globalVars.licenseType;
                }
                this.globalVars.user.lastLoginDate = new Date();
                this.globalVars.user.registratedDate = new Date();
                this.globalVars.user.userRegion = auxRegion;
                this.globalVars.licenseType = auxRegion;
                this.globalVars.rootScope.cookiesService.setCookie(
                  CookieNames.CodereUser,
                  data.username,
                  this.globalVars.rootScope.cookiesService.YEAR_MILLISECONDS,
                );
                this.globalVars.user.id = data.id;
                if (this.globalVars.licenseType === LicenseTypes.Nacional) {
                  this.globalVars.rootScope.cookiesService.setCookiePerUser(
                    CookieNames.MigrationCompleted,
                    data.username,
                    this.globalVars.rootScope.cookiesService.YEAR_MILLISECONDS,
                  );
                  try {
                    await this.utilsMsoService.setGTMTrackers();
                  } catch (error) {
                    console.error(error);
                  }
                }
                if (this.globalVars.FEATURES.MSO_GraceTimeLogicEnabled) {
                  this.dataUserService.setExtraUserData = {
                    graceTime: GraceTimeStatus.ON_TIME,
                    documentationVerification: false,
                  };
                  if (this.globalVars.licenseType === LicenseTypes.Panama) {
                    localStorage.setItem("paramsPaAfterReg", JSON.stringify({ comeFromReg: true }));
                  }
                }
                if (this.globalVars.licenseType === LicenseTypes.Colombia) {
                  this.globalVars.extraUserData.pptNotVerification = !!data["isPPT"];
                  if (this.globalVars.partner === "mvp") {
                    this.globalVars.mvpData.login = "1";
                    sessionStorage.setItem(
                      "partnercodere",
                      JSON.stringify({ partner: this.globalVars.partner, mvpData: this.globalVars.mvpData }),
                    );
                  }
                }
                if (doSetRoot) {
                  await this.router.navigate([dest]);
                }
                this.events.publish("user:login");
                if (!this.globalVars.FEATURES.ModalBonusSportCasino) {
                  this.events.publish(
                    "cashier:open",
                    params
                      ? { cashierEmpty: false, section: "deposit", ...params }
                      : { cashierEmpty: false, section: "deposit" },
                  );
                }
                resolve(true);
              } else {
                resolve(false);
              }
            }
          },
          (err) => {
            resolve(false);
          },
        );
      }
    });
  }
}
